import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Alert, 
  Button, 
  Spinner,
  Badge,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Label
} from 'reactstrap';
import axios from '../../api/Axios';

const TutorSubjects = () => {
  // State management
  const [subjects, setSubjects] = useState([]);
  const [tutorBranches, setTutorBranches] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tutorCategory, setTutorCategory] = useState('STEM');

  // Teaching level options
  const teachingLevels = [
    { id: 'undergraduate', label: 'Undergraduate', color: 'info' },
    { id: 'honors', label: 'Honours', color: 'primary' },
    { id: 'masters', label: 'Masters', color: 'success' },
    { id: 'doctorate', label: 'Doctorate', color: 'warning' }
  ];

  // Load data on component mount
  useEffect(() => {
    console.log('TutorSubjects component mounted!');
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get user ID from localStorage
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Fetch subjects and branches
      const subjectsResponse = await axios.get('/api/subjects/');
      console.log('Subjects loaded:', subjectsResponse.data);
      setSubjects(subjectsResponse.data);

      // Fetch tutor profile to get category and existing branches
      const profileResponse = await axios.get(`/accounts/tutors/${userId}/profile/get/`);
      const tutorData = profileResponse.data;
      console.log('Tutor profile loaded:', tutorData);
      
      setTutorCategory(tutorData.subject_category || 'STEM');
      
      // Fetch tutor branches with teaching levels
      const branchesResponse = await axios.get(`/accounts/tutors/${userId}/branches/`);
      console.log('Tutor branches loaded:', branchesResponse.data);
      setTutorBranches(branchesResponse.data);

      // Set selected subjects based on current branches
      const currentSubjectIds = [...new Set(
        branchesResponse.data.map(tb => tb.branch_details.subject_id)
      )];
      console.log('Selected subject IDs:', currentSubjectIds);
      setSelectedSubjects(currentSubjectIds);

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response) {
        // API returned an error response
        console.error('API Error:', error.response.data);
        setError(`API Error: ${error.response.data.error || error.response.statusText}`);
      } else if (error.request) {
        // Network error
        console.error('Network Error:', error.request);
        setError('Network error - please check your connection');
      } else {
        // Other error
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter subjects based on tutor's category
  const filteredSubjects = subjects.filter(subject => subject.category === tutorCategory);

  // Handle adding a subject (automatically adds all its branches with no levels selected)
  const handleAddSubject = (subjectId) => {
    console.log('Adding subject:', subjectId);
    
    if (!selectedSubjects.includes(subjectId)) {
      setSelectedSubjects([...selectedSubjects, subjectId]);
      
      // Add all branches from this subject with empty teaching levels
      const subject = subjects.find(s => s.id === subjectId);
      console.log('Found subject:', subject);
      
      if (subject && subject.branches) {
        const newBranches = subject.branches.map(branch => ({
          id: null, // New entry, no ID yet
          branch_id: branch.id,
          branch_details: {
            id: branch.id,
            name: branch.name,
            subject_id: subjectId,
            subject_name: subject.name
          },
          teaching_levels: []
        }));
        
        console.log('New branches to add:', newBranches);
        
        // Filter out any existing branches for this subject to avoid duplicates
        const filteredExistingBranches = tutorBranches.filter(
          tb => tb.branch_details.subject_id !== subjectId
        );
        
        const updatedBranches = [...filteredExistingBranches, ...newBranches];
        console.log('Updated tutor branches:', updatedBranches);
        
        setTutorBranches(updatedBranches);
      }
    }
  };

  // Handle removing a subject (removes all its branches)
  const handleRemoveSubject = (subjectId) => {
    setSelectedSubjects(selectedSubjects.filter(id => id !== subjectId));
    setTutorBranches(tutorBranches.filter(tb => tb.branch_details.subject_id !== subjectId));
  };

  // Handle teaching level toggle for a specific branch
  const handleTeachingLevelToggle = (branchId, level) => {
    console.log('Toggling level:', level, 'for branch:', branchId);
    
    setTutorBranches(tutorBranches.map(tb => {
      if (tb.branch_id === branchId) {
        const currentLevels = tb.teaching_levels || [];
        const hasLevel = currentLevels.includes(level);
        
        const newLevels = hasLevel 
          ? currentLevels.filter(l => l !== level)
          : [...currentLevels, level];
          
        console.log('Branch', branchId, 'levels updated from', currentLevels, 'to', newLevels);
        
        return {
          ...tb,
          teaching_levels: newLevels
        };
      }
      return tb;
    }));
  };

  // Handle saving all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Prepare data for API
      const branchesWithLevels = tutorBranches
        .filter(tb => tb.teaching_levels && tb.teaching_levels.length > 0)
        .map(tb => ({
          branch_id: tb.branch_id,
          teaching_levels: tb.teaching_levels
        }));

      console.log('Saving branches with levels:', branchesWithLevels);

      const userId = localStorage.getItem('user_id');
      const response = await axios.post(`/accounts/tutors/${userId}/branches/update/`, {
        branches: branchesWithLevels
      });

      console.log('Save response:', response.data);
      setSuccess('Subjects and teaching levels updated successfully!');
      
      // Refresh data to get updated info with IDs
      await fetchData();

    } catch (error) {
      console.error('Error saving branches:', error);
      if (error.response) {
        console.error('Save API Error:', error.response.data);
        setError(`Save failed: ${error.response.data.error || error.response.statusText}`);
      } else if (error.request) {
        setError('Network error during save - please check your connection');
      } else {
        setError(`Save error: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Group branches by subject for display
  const groupedBranches = selectedSubjects.reduce((acc, subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (subject) {
      const subjectBranches = tutorBranches.filter(
        tb => tb.branch_details.subject_id === subjectId
      );
      
      if (subjectBranches.length > 0) {
        acc[subjectId] = {
          subject: subject,
          branches: subjectBranches
        };
      }
    }
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner size="lg" color="primary" />
        <p className="mt-3 text-muted">Loading your subjects...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <Card className="mb-4" style={{ borderLeft: '4px solid #17a2b8' }}>
        <CardBody>
          <CardTitle tag="h4" className="mb-3">
            <i className="bi bi-book me-2" style={{ color: '#17a2b8' }}></i>
            My Subjects & Teaching Levels
          </CardTitle>
          <p className="lead mb-2">
            Manage the subjects and branches you can teach, and specify your teaching levels for each branch.
          </p>
          <Badge color="info" className="me-2">Category: {tutorCategory}</Badge>
          <Badge color="secondary">{tutorBranches.filter(tb => tb.teaching_levels?.length > 0).length} Active Branches</Badge>
          <Badge color="primary" className="ms-2">Last Update: {new Date().toLocaleTimeString()}</Badge>
        </CardBody>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert color="danger" toggle={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" toggle={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Debug Info - Remove in production */}
      <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
        <CardBody>
          <small>
            <strong>Debug:</strong> {tutorBranches.length} total branches loaded, 
            {selectedSubjects.length} subjects selected
            <Button 
              size="sm" 
              color="outline-secondary" 
              className="ms-2"
              onClick={() => console.log('Current state:', { tutorBranches, selectedSubjects })}
            >
              Log State
            </Button>
          </small>
        </CardBody>
      </Card>

      <Row>
        {/* Add Subject Section */}
        <Col lg="4" className="mb-4">
          <Card>
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-plus-circle me-2"></i>
                Add Subject
              </CardTitle>
              <p className="text-muted small">
                Add subjects from your {tutorCategory} category:
              </p>
              
              <div className="d-grid gap-2">
                {filteredSubjects
                  .filter(subject => !selectedSubjects.includes(subject.id))
                  .map(subject => (
                    <Button
                      key={subject.id}
                      color="outline-primary"
                      size="sm"
                      onClick={() => handleAddSubject(subject.id)}
                      className="text-start"
                    >
                      <i className="bi bi-book me-2"></i>
                      {subject.name}
                      <Badge color="secondary" className="ms-2">
                        {subject.branches?.length || 0} branches
                      </Badge>
                    </Button>
                  ))
                }
                
                {filteredSubjects.every(s => selectedSubjects.includes(s.id)) && (
                  <div className="text-muted small text-center py-3">
                    <i className="bi bi-check-circle me-1"></i>
                    All {tutorCategory} subjects added
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Current Subjects & Branches */}
        <Col lg="8">
          {Object.keys(groupedBranches).length > 0 ? (
            <div>
              {Object.values(groupedBranches).map(({ subject, branches }) => (
                <Card key={subject.id} className="mb-4">
                  <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-book me-2"></i>
                      <span className="fw-bold">{subject.name}</span>
                    </div>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveSubject(subject.id)}
                      title="Remove this subject"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      Select the teaching levels for each branch you can teach:
                    </p>
                    
                    <Row>
                      {branches.map(tutorBranch => (
                        <Col md="6" className="mb-4" key={tutorBranch.branch_id}>
                          <div className={`border rounded p-3 h-100 ${
                            tutorBranch.teaching_levels?.length > 0 
                              ? 'border-success bg-light' 
                              : 'border-warning'
                          }`}>
                            <h6 className="fw-bold mb-3">
                              <i className="bi bi-arrow-right-circle me-2"></i>
                              {tutorBranch.branch_details.name}
                              {tutorBranch.teaching_levels?.length > 0 && (
                                <Badge color="success" className="ms-2" pill>
                                  {tutorBranch.teaching_levels.length}
                                </Badge>
                              )}
                            </h6>
                            
                            <p className="small text-muted mb-3">
                              Select which levels you can teach for this branch:
                            </p>
                            
                            <div className="d-grid gap-2">
                              {teachingLevels.map(level => {
                                const isChecked = tutorBranch.teaching_levels?.includes(level.id) || false;
                                return (
                                  <div key={level.id} className="form-check d-flex align-items-center">
                                    <Input
                                      type="checkbox"
                                      className="form-check-input me-2"
                                      checked={isChecked}
                                      onChange={() => {
                                        console.log(`Clicked ${level.label} for branch ${tutorBranch.branch_details.name}`);
                                        handleTeachingLevelToggle(tutorBranch.branch_id, level.id);
                                      }}
                                    />
                                    <Label className="form-check-label">
                                      <Badge color={level.color} className="px-2 py-1">
                                        {level.label}
                                      </Badge>
                                      {isChecked && <i className="bi bi-check-lg text-success ms-2"></i>}
                                    </Label>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Show selected levels summary */}
                            {tutorBranch.teaching_levels?.length > 0 ? (
                              <div className="mt-2 pt-2 border-top">
                                <small className="text-success">
                                  <i className="bi bi-check-lg me-1"></i>
                                  {tutorBranch.teaching_levels.length} level(s) selected
                                </small>
                              </div>
                            ) : (
                              <div className="mt-2 pt-2 border-top">
                                <small className="text-muted">
                                  <i className="bi bi-exclamation-circle me-1"></i>
                                  No levels selected - select at least one level to teach this branch
                                </small>
                              </div>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>
              ))}

              {/* Save Button */}
              <div className="d-flex justify-content-end">
                <Button
                  color="primary"
                  size="lg"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Card>
              <CardBody className="text-center py-5">
                <i className="bi bi-book" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                <h5 className="mt-3 text-muted">No Subjects Selected</h5>
                <p className="text-muted">
                  Add subjects from the panel on the left to start configuring your teaching levels.
                </p>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TutorSubjects;